import { Location } from "@angular/common";
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup, UntypedFormControl, Validators } from "@angular/forms";
import { AccountService, AuthService } from "@app/core";
import { LocationWidgetSettings } from "@app/models";
import { WidgetSettingsModalBaseComponent } from "@app/pages/components/widget-settings/widget-settings-modal-base";
import { LandingService, WidgetService } from "@app/services";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { AllowedDeliveryChannel, LocationWidgetSettingsGetDto, LocationWithDefaultWidgetGetDto, nullifyFalsyValues } from "clearline-api";
import { WidgetType } from "clearline-common";
import { RoleTypes } from "core-kit";

@Component({
  selector: "app-widget-settings-modal",
  templateUrl: "./widget-settings-modal.component.html",
  styleUrls: ["./widget-settings-modal.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WidgetSettingsModalComponent extends WidgetSettingsModalBaseComponent implements OnInit, AfterViewInit {
  constructor(
    public activeModal: NgbActiveModal,
    public landingSvc: LandingService,
    public widgetSvc: WidgetService,
    private accountService: AccountService,
    private authService: AuthService,
    private cdf: ChangeDetectorRef,
    private locationUrl: Location
  ) {
    super(activeModal);

    this.staticUrl = window.location.pathname;
    this.locationUrl.onUrlChange((x) => this.urlChange(x));
  }

  canApplySettingsToAllLocations = false;
  createMode: boolean;
  isLoadPage = true;
  widgetType = WidgetType;
  loading = true;
  hasThumbnailPreview = false;
  staticUrl: string;
  widgetsInThePlan: LocationWithDefaultWidgetGetDto[];

  @ViewChild("widget") widget;

  private _locationWidget: LocationWidgetSettings;

  get widgetSettings(): LocationWidgetSettingsGetDto {
    const value = [WidgetType.SmartPage, WidgetType.OneTimePayment].includes(this.baseWidgetType)
      ? this.widget?.form.getRawValue()
      : this.widget?.form.value;
    const widgetSettingsJson = JSON.stringify(value);

    return {
      deliveryMethod: this.form.controls.deliveryMethod.value,
      widgetSettingsJson,
      configured: this.form.controls.configured.value,
      useForAllLocations: this.form.controls.useForAllLocations.value
    } as LocationWidgetSettingsGetDto;
  }

  get isAdmin() {
    return this.authService.requiredMinimumRole(RoleTypes.PartnerAdmin);
  }

  get isValid() {
    return this.form.valid && (!this.widget || this.widget?.form.valid);
  }

  set locationWidget(value: LocationWidgetSettings) {
    this._locationWidget = value;
    this.form.patchValue(
      {
        deliveryMethod: value.deliveryMethod,
        configured: value.configured
      },
      { emitEvent: false }
    );
  }

  form = new FormGroup({
    widgetSettings: new UntypedFormControl(""),
    deliveryMethod: new UntypedFormControl("", [Validators.required]),
    configured: new FormControl(),
    useForAllLocations: new FormControl(false)
  });

  ngOnInit(): void {
    this.form.patchValue(
      {
        configured: this.configured,
        deliveryMethod: this.deliveryMethod ? this.deliveryMethod : AllowedDeliveryChannel.qr,
        useForAllLocations: false
        // useForAllLocations: this.useForAllLocations
      },
      { emitEvent: false }
    );

    if (!this.canApplySettingsToAllLocations) {
      this.form.controls.useForAllLocations.disable({ emitEvent: false });
    }

    if (this.widgetId) {
      this.loadLandingPage();
    } else {
      this.loading = false;
    }
  }

  ngAfterViewInit() {
    this.cdf.detectChanges();
  }

  previewPage() {
    if (this.createMode) {
      this.openTestPage();
      return;
    }

    if (!this.isValid) {
      return;
    }

    if (this.brand && this.brand.id > 0) {
      this.widgetSvc.updateLocationCampaign(this.getLocationWidgetSettings()).subscribe(() => {
        const url = this.landingSvc.getProdLandingPageUrl(this.page.id, this.brand.shortName, this.location.id, this.widgetId);
        window.open(url, "_blank");
      });
    }
  }

  openTestPage() {
    const url = this.landingSvc.getLandingPageUrl(this.page.id, true);
    window.open(url, "_blank");
  }

  save() {
    if (!this.isValid) {
      return;
    }

    const result = this.getLocationWidgetSettings();
    this.activeModal.close(result);
  }

  setWidgetSettingsValidation() {
    this.form.controls.widgetSettings.markAsTouched();
    setTimeout(() => this.cdf.detectChanges(), 100);
  }

  urlChange(x) {
    if (x && !x.includes(this.staticUrl)) {
      this.activeModal.close();
    }
  }

  private getLocationWidgetSettings(): LocationWidgetSettings {
    const settings = {
      ...this._locationWidget,
      configured: this.form.controls.configured.value,
      deliveryMethod: this.form.controls.deliveryMethod.value,
      useForAllLocations: this.form.controls.useForAllLocations.value,
      widgetSettingsJson:
        this.baseWidgetType === WidgetType.OneTimePayment ? this.serializeNullifiedWidgetSettings() : this.widgetSettings.widgetSettingsJson
    };

    return settings;
  }

  private serializeNullifiedWidgetSettings(): string {
    const widgetSettings = nullifyFalsyValues(JSON.parse(this.widgetSettings.widgetSettingsJson));
    return JSON.stringify(widgetSettings);
  }

  private loadLandingPage() {
    this.landingSvc.getByWidgetId(this.widgetId).subscribe(
      (p) => {
        this.page = p;
        this.hasThumbnailPreview =
          !!this.page && !this.isCustomMapp && this.widgetId != this.widgetType.RequestReview && this.widgetId != this.widgetType.SmartPage;
        this.loading = false;
        this.cdf.detectChanges();
      },
      () => {
        this.loading = false;
        this.cdf.detectChanges();
      }
    );
  }
}
